import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OperatorPageShell, Button } from '@/components';
import { useAuthFetch } from '@/hooks';
import { useAuthUser } from '@/auth.ts';
import { formatDateTimeFull } from '@/utils';
import { ROUTES } from '../../routes.ts';
import { PAGE_SIZE, TABLE_BTN } from './operatorTableConstants.ts';
import { Th, OperatorPagination, AccountStatusPill } from './operatorTableHelpers.tsx';
import panel from './operatorPanel.module.css';
import type { OperatorPatientListResponse, OperatorPatientSummary, OperatorRevealEmailResponse } from '@dermalink/shared';

export function OperatorPatientsPanel() {
  const authFetch = useAuthFetch();
  const [data, setData]       = useState<OperatorPatientListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [page, setPage]       = useState(1);
  const [q, setQ]             = useState('');
  const [qInput, setQInput]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    const params = new URLSearchParams({ page: String(page) });
    if (q) params.set('q', q);
    authFetch(`/operator/patients?${params}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: OperatorPatientListResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [authFetch, page, q]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(qInput.trim());
    setPage(1);
  }

  return (
    <div className={panel.section}>
      <form onSubmit={handleSearch} className={panel.toolbar}>
        <input
          type="text"
          placeholder="Zoek op naam of e-mailadres"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          className={panel.field}
          style={{ minWidth: '260px' }}
        />
        <Button type="submit" variant="ghost">Zoeken</Button>
      </form>

      {loading && <p className="text-muted status-message">Laden…</p>}
      {error   && <p className="text-muted status-message">Kon patiëntenoverzicht niet laden.</p>}

      {data && !loading && (
        <>
          <div className={panel.resultCount}>{data.total} {data.total === 1 ? 'patiënt' : 'patiënten'}</div>
          <div className={panel.tableWrap}>
            <table className={panel.table}>
              <thead>
                <tr>
                  <Th>Actor sub</Th>
                  <Th>Aangemaakt op</Th>
                  <Th>Laatste login</Th>
                  <Th>Status</Th>
                  <Th>Acties</Th>
                </tr>
              </thead>
              <tbody>
                {data.patients.map((p) => <PatientRow key={p.cognito_sub} patient={p} onChanged={load} />)}
                {data.patients.length === 0 && (
                  <tr><td colSpan={5} className={panel.emptyCell}>Geen patiënten gevonden.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <OperatorPagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

export function OperatorPatients() {
  return (
    <OperatorPageShell maxWidth="1400px">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to={ROUTES.operator} className={panel.mutedText}>← Beheerder</Link>
        <h1 className="page-heading" style={{ margin: 0 }}>Patiënten</h1>
      </div>
      <OperatorPatientsPanel />
    </OperatorPageShell>
  );
}

function PatientRow({ patient: p, onChanged }: { patient: OperatorPatientSummary; onChanged: () => void }) {
  const authFetch = useAuthFetch();
  const authUser   = useAuthUser();
  const isSelf     = p.cognito_sub === authUser?.userId;
  const suspended = p.suspended_at !== null;

  const [reason, setReason]   = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState(false);

  const [email, setEmail]               = useState<string | null>(null);
  const [emailReason, setEmailReason]   = useState('');
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError]     = useState(false);

  async function handleDeactivate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(false);
    try {
      const res = await authFetch(`/operator/users/${p.cognito_sub}/suspend`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ reason: reason.trim() || undefined }),
      });
      if (!res.ok) throw new Error();
      setEditing(false);
      setReason('');
      onChanged();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  async function handleReactivate() {
    if (!confirm(`Account ${p.cognito_sub} heractiveren?`)) return;
    setSaving(true);
    setSaveError(false);
    try {
      const res = await authFetch(`/operator/users/${p.cognito_sub}/unsuspend`, { method: 'POST' });
      if (!res.ok) throw new Error();
      onChanged();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  function handleToggleEmail() {
    if (email !== null) { setEmail(null); return; }
    setEmailEditing(true);
  }

  async function handleRevealEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!emailReason.trim()) return;
    setEmailLoading(true);
    setEmailError(false);
    try {
      const res = await authFetch(`/operator/users/${p.cognito_sub}/email`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ reason: emailReason.trim() }),
      });
      if (!res.ok) throw new Error();
      const body = await res.json() as OperatorRevealEmailResponse;
      setEmail(body.email ?? '—');
      setEmailEditing(false);
      setEmailReason('');
    } catch {
      setEmailError(true);
    } finally {
      setEmailLoading(false);
    }
  }

  return (
    <tr>
      <td className={panel.td} style={{ fontFamily: 'monospace' }}>{p.cognito_sub}</td>
      <td className={panel.td} style={{ whiteSpace: 'nowrap' }}>{formatDateTimeFull(p.created_at)}</td>
      <td className={panel.td} style={{ whiteSpace: 'nowrap' }}>{formatDateTimeFull(p.last_login_at)}</td>
      <td className={panel.td}>
        <AccountStatusPill suspended={suspended} reason={p.suspended_reason} />
      </td>
      <td className={panel.td}>
        {editing ? (
          <form onSubmit={handleDeactivate} className={panel.inlineForm}>
            <input
              type="text"
              autoFocus
              placeholder="Reden (optioneel)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={`${panel.field} ${panel.fieldSm}`}
              style={{ width: '160px' }}
            />
            <Button type="submit" variant="danger" style={TABLE_BTN} disabled={saving}>{saving ? '…' : 'Deactiveren'}</Button>
            <Button type="button" variant="ghost" style={TABLE_BTN} onClick={() => setEditing(false)}>Annuleer</Button>
          </form>
        ) : emailEditing ? (
          <form onSubmit={handleRevealEmail} className={panel.inlineForm}>
            <input
              type="text"
              autoFocus
              required
              placeholder="Reden (verplicht)"
              value={emailReason}
              onChange={(e) => setEmailReason(e.target.value)}
              className={`${panel.field} ${panel.fieldSm}`}
              style={{ width: '160px' }}
            />
            <Button type="submit" variant="primary" style={TABLE_BTN} disabled={emailLoading || !emailReason.trim()}>{emailLoading ? '…' : 'Toon email'}</Button>
            <Button type="button" variant="ghost" style={TABLE_BTN} onClick={() => { setEmailEditing(false); setEmailReason(''); }}>Annuleer</Button>
          </form>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {isSelf ? (
              <span className={panel.mutedText}>Dit ben jij</span>
            ) : suspended ? (
              <Button variant="ghost" style={TABLE_BTN} onClick={handleReactivate} disabled={saving}>
                {saving ? '…' : 'Heractiveren'}
              </Button>
            ) : (
              <Button variant="danger" style={TABLE_BTN} onClick={() => setEditing(true)} disabled={saving}>
                Deactiveren
              </Button>
            )}
            <Button variant="ghost" style={TABLE_BTN} onClick={handleToggleEmail}>
              {email !== null ? 'Verbergen' : 'Toon email'}
            </Button>
            {email !== null && <span style={{ fontSize: 'var(--text-sm)' }}>{email}</span>}
          </div>
        )}
        {saveError  && <span className={panel.errorText} style={{ marginLeft: '0.5rem' }}>Mislukt</span>}
        {emailError && <span className={panel.errorText} style={{ marginLeft: '0.5rem' }}>Ophalen mislukt</span>}
      </td>
    </tr>
  );
}
